import { Collapsible, Stack } from '@chakra-ui/react'
import { useDroppable } from '@dnd-kit/core'
import cn from 'clsx'
import { FC, useMemo, useState } from 'react'

import { Button, Typography } from '@/components/ui'

import DownloadSimple from '@/assets/icons/download-simple.svg'
import KeyboardArrowRight from '@/assets/icons/keyboard_arrow_right.svg'
import Link from '@/assets/icons/link.svg'
import Note from '@/assets/icons/note.svg'
import PencilSimple from '@/assets/icons/pencil-simple.svg'
import Trash from '@/assets/icons/trash.svg'

import {
	IDeleteHighlightParams,
	IHighlightsFolder,
	ISegment
} from '@/types/highlight.interface'

import { getHighlightsDuration } from '../helpers'

import DescriptionTooltip from './DescriptionTooltip'
import FolderColorBalls from './FolderColorBalls'
import HighlightItem from './HighlightItem'

interface IHighlightFolderItemProps {
	folder: IHighlightsFolder
	onOpenDeleteFolder: (folderId: number) => void
	deleteHighlight: (
		params: IDeleteHighlightParams
	) => Promise<unknown> | unknown
	onEditFolder: (folderId: number) => void
	isSelectMode: boolean
	onToggleFolder: (folderId: number) => void
	isFolderSelected: boolean
	downloadFolder: (folder: IHighlightsFolder) => void
	downloadSegment: (params: {
		segment: ISegment
		folder: IHighlightsFolder
	}) => void
	shareFolder: (folderId: number) => void
	isSharedView?: boolean
	onScrollToSegment?: (segmentStart: number) => void
}

const HighlightFolderItem: FC<IHighlightFolderItemProps> = ({
	folder,
	onOpenDeleteFolder,
	deleteHighlight,
	onEditFolder,
	isSelectMode,
	onToggleFolder,
	isFolderSelected,
	downloadFolder,
	downloadSegment,
	shareFolder,
	isSharedView = false,
	onScrollToSegment
}) => {
	const [descriptionTooltip, setDescriptionTooltip] = useState<{
		x: number
		y: number
		description: string
	} | null>(null)

	const { setNodeRef: setDroppableNodeRef, isOver } = useDroppable({
		id: `folder:${folder.id}`,
		data: {
			type: 'folder',
			folderId: folder.id
		},
		disabled: isSharedView
	})

	const totalTimeHighlights = useMemo(
		() => getHighlightsDuration(folder.segments),
		[folder]
	)

	const FolderSelectIndicator = ({ checked }: { checked: boolean }) => (
		<span
			className={cn(
				'flex h-4 w-4 items-center justify-center rounded-full border border-neutral-400',
				checked && 'border-neutral-500'
			)}
		>
			{checked && <span className='h-2 w-2 rounded-full bg-neutral-500' />}
		</span>
	)

	return (
		<>
			<Collapsible.Root
				ref={setDroppableNodeRef}
				className={cn(
					'group/folder-root hover:bg-neutral-100',
					isFolderSelected && 'bg-neutral-100',
					isOver && 'bg-neutral-100 ring-1 ring-neutral-300'
				)}
			>
				<div className='group/folder-header relative flex items-center'>
					<Collapsible.Trigger
						width='100%'
						paddingY='3'
						paddingLeft='3'
						paddingRight={isSelectMode ? '12' : !isSharedView ? '36' : '2'}
						display='flex'
						gap='2'
						alignItems='center'
						className='group/folder-trigger'
					>
						{/* Folder information */}
						<div className='flex items-center gap-3'>
							{/* Color */}
							<div className='flex group-hover/folder-trigger:hidden group-hover/folder-root:hidden flex-col gap-[2px]'>
								<FolderColorBalls colorCode={folder.color_code} />
							</div>

							{/* Arrow */}
							<Collapsible.Context>
								{({ open }) => (
									<KeyboardArrowRight
										className={cn(
											'w-3 h-3 transition-transform hidden group-hover/folder-trigger:flex group-hover/folder-root:flex',
											open && 'rotate-90'
										)}
									/>
								)}
							</Collapsible.Context>

							<div className='flex flex-col gap-1'>
								{/* Folder name */}
								<Typography
									variant='text'
									className='text-neutral-900 text-start leading-[112%]'
								>
									{folder.name}
								</Typography>

								{/* Time */}
								<div className='flex items-center'>
									<Typography
										variant='text'
										className='!text-[12px] text-neutral-700'
									>
										{folder.segments.length} highlights,{' '}
										{totalTimeHighlights || '00:00:00'}
										{folder.description && <span>, </span>}
									</Typography>
									<div
										className='relative group/description'
										onClick={e => e.stopPropagation()}
									>
										{folder.description && (
											<Note
												className='w-3 h-3 text-neutral-400 cursor-pointer'
												onMouseEnter={event => {
													const rect =
														event.currentTarget.getBoundingClientRect()

													setDescriptionTooltip({
														x: rect.left,
														y: rect.bottom + 6,
														description: folder.description!
													})
												}}
												onMouseLeave={() => setDescriptionTooltip(null)}
											/>
										)}
									</div>
								</div>
							</div>
						</div>
					</Collapsible.Trigger>

					{/* Buttons block */}
					{isSelectMode ? (
						<button
							type='button'
							role='checkbox'
							aria-checked={isFolderSelected}
							className='absolute right-2.5 top-1/2 -translate-y-1/2 cursor-pointer'
							onClick={e => {
								e.stopPropagation()
								onToggleFolder(folder.id)
							}}
						>
							<FolderSelectIndicator checked={isFolderSelected} />
						</button>
					) : !isSharedView ? (
						<div className='absolute right-2.5 top-1/2 -translate-y-1/2 flex pointer-events-none opacity-0 group-hover/folder-header:opacity-100 group-hover/folder-header:pointer-events-auto'>
							<Button
								className='!p-[6px] h-fit bg-neutral-100 hover:bg-neutral-200 rounded-full'
								onClick={e => {
									e.stopPropagation()
									onEditFolder(folder.id)
								}}
							>
								<PencilSimple className='w-4 h-4 text-neutral-400 cursor-pointer' />
							</Button>
							<Button
								className='!p-[6px] h-fit bg-neutral-100 hover:bg-neutral-200 rounded-full'
								onClick={e => {
									e.stopPropagation()
									downloadFolder(folder)
								}}
							>
								<DownloadSimple className='w-4 h-4 text-neutral-400 cursor-pointer' />
							</Button>
							<Button
								className='!p-[6px] h-fit bg-neutral-100 hover:bg-neutral-200 rounded-full'
								onClick={e => {
									e.stopPropagation()
									shareFolder(folder.id)
								}}
							>
								<Link className='w-4 h-4 text-neutral-400 cursor-pointer' />
							</Button>
							<Button
								className='!p-[6px] h-fit bg-neutral-100 hover:bg-neutral-200 rounded-full'
								onClick={e => {
									e.stopPropagation()
									onOpenDeleteFolder(folder.id)
								}}
							>
								<Trash className='w-4 h-4 text-neutral-400 cursor-pointer' />
							</Button>
						</div>
					) : null}
				</div>

				<Collapsible.Content>
					<Stack gap='0' marginLeft='3'>
						{folder.segments.map((segment, index) => (
							<HighlightItem
								key={`${segment.start}-${segment.end}-${index}`}
								index={index}
								folderId={folder.id}
								segment={segment}
								deleteHighlight={deleteHighlight}
								downloadSegment={segment =>
									downloadSegment({
										segment,
										folder
									})
								}
								isSharedView={isSharedView}
								onScrollToSegment={onScrollToSegment}
							/>
						))}
					</Stack>
				</Collapsible.Content>
			</Collapsible.Root>

			{descriptionTooltip && (
				<DescriptionTooltip
					x={descriptionTooltip.x}
					y={descriptionTooltip.y}
					description={descriptionTooltip.description}
				/>
			)}
		</>
	)
}

export default HighlightFolderItem
